import { ArrowRight, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { siteData } from '../../data/mock';
import { useInView } from '../../hooks/useInView';

export const PricingTeaser = () => {
  const { pricing, addOns } = siteData;
  const [ref, isInView] = useInView();
  const [addRef, addInView] = useInView();

  return (
    <>
      <section id="pricing" className="py-[90px] lg:py-[110px] bg-white">
        <div
          ref={ref}
          className={`max-w-[1200px] mx-auto px-6 lg:px-8 ${
            isInView ? 'animate-fade-in-up' : 'opacity-0'
          }`}
        >
          <h2 className="font-heading text-[1.85rem] lg:text-[2.5rem] font-bold text-stone-900 leading-[1.15] tracking-[-0.01em]">
            {pricing.headline}
          </h2>

          {/* Pricing Cards */}
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-[640px]">
            {/* Monthly - highlighted */}
            <div className="p-6 rounded-2xl bg-[#edf5f2] border-2 border-[#2d6a5a]/20 shadow-[0_2px_8px_rgba(45,106,90,0.06)]">
              <p className="text-[2rem] font-heading font-bold text-[#2d6a5a] leading-none">
                {pricing.monthly.price}
              </p>
              <p className="mt-1.5 text-[0.84rem] text-stone-600 font-medium">
                {pricing.monthly.period}
              </p>
            </div>

            {/* Annual */}
            <div className="p-6 rounded-2xl bg-[#faf9f6] border border-stone-200/60">
              <p className="text-[2rem] font-heading font-bold text-stone-700 leading-none">
                {pricing.annual.price}
              </p>
              <p className="mt-1.5 text-[0.84rem] text-stone-500">
                {pricing.annual.period}{' '}
                <span className="text-stone-400">({pricing.annual.note})</span>
              </p>
              <p className="mt-1 text-[0.8rem] text-[#2d6a5a] font-medium">
                {pricing.annual.savings}
              </p>
            </div>
          </div>

          <p className="mt-8 text-[0.95rem] text-stone-500 leading-[1.7] max-w-[520px]">
            {pricing.summary}
          </p>

          {/* Included Features */}
          <div className="mt-10">
            <p className="text-[0.82rem] font-semibold text-stone-400 uppercase tracking-wider mb-4">
              Includes:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5 max-w-[560px]">
              {pricing.includes.map((item, index) => (
                <div key={index} className="flex items-center gap-2.5">
                  <div className="w-4 h-4 rounded-full bg-[#2d6a5a]/10 flex items-center justify-center flex-shrink-0">
                    <Check size={9} className="text-[#2d6a5a]" />
                  </div>
                  <span className="text-stone-600 text-[0.88rem]">{item}</span>
                </div>
              ))}
            </div>
          </div>

          <p className="mt-8 text-[0.88rem] text-stone-400 leading-[1.7] max-w-[520px]">
            {pricing.closing}
          </p>

          {/* CTA */}
          <div className="mt-10">
            <Button
              asChild
              className="bg-[#2d6a5a] hover:bg-[#245549] text-white rounded-full px-7 h-12 text-[0.92rem] font-medium hover:shadow-lg hover:shadow-[#2d6a5a]/15"
              style={{ transition: 'background-color 0.2s, box-shadow 0.3s' }}
            >
              <Link to={pricing.cta.href}>
                {pricing.cta.label}
                <ArrowRight className="ml-2" size={17} />
              </Link>
            </Button>
            <div className="mt-4 space-y-0.5">
              {pricing.microcopy.map((line, index) => (
                <p key={index} className="text-[0.8rem] text-stone-400">
                  {line}
                </p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Optional Add-Ons */}
      <section className="py-[70px] lg:py-[80px] bg-[#faf9f6] border-t border-stone-200/40">
        <div
          ref={addRef}
          className={`max-w-[1200px] mx-auto px-6 lg:px-8 ${
            addInView ? 'animate-fade-in-up' : 'opacity-0'
          }`}
        >
          <h3 className="font-heading text-[1.25rem] lg:text-[1.5rem] font-bold text-stone-900 tracking-[-0.01em]">
            {addOns.headline}
          </h3>

          <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-[640px]">
            {addOns.items.map((item, index) => (
              <div
                key={index}
                className="p-5 rounded-2xl bg-white border border-stone-200/50"
              >
                <div className="flex items-center gap-2.5 mb-2.5">
                  <h4 className="font-heading text-[0.92rem] font-semibold text-stone-900">
                    {item.title}
                  </h4>
                  <span className="text-[0.7rem] text-stone-400 font-medium px-2 py-0.5 rounded-full bg-stone-100 border border-stone-200/50">
                    {item.tag}
                  </span>
                </div>
                <p className="text-[0.84rem] text-stone-500 leading-[1.65]">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};
